/**
 * Vente à terme strategy.
 *
 * - SPV owns and finances the full project through delivery.
 * - Presales secure revenue; deposits stay restricted unless depositUsablePct > 0.
 * - Construction remains an SPV cost.
 * - The cash path includes the structurally required construction facility.
 *   Any part not yet committed in P&L remains an explicit funding gap.
 * - Buyer mortgage proceeds become usable at delivery.
 * - Delivery proceeds repay bank debt before capital and profit become available.
 * - Construction facility *required* is computed independently of *secured*.
 */

import { n } from '../math.js';
import {
  constructionPctAtMonth,
  evaluateConstructionBankConditions,
  evaluateLandBankConditions,
} from './bankConditions.js';
import { resolveSecuredFacilities, securedBankFacilityCap } from './bankFacilities.js';

/**
 * Month-level funding decision for vente à terme (actual cash path).
 */
export function vatMonthDecision(ctx) {
  const {
    month,
    programme,
    pnl,
    assumptions,
    salesRow,
    costs,
    state,
    plannedConstructionFacility = 0,
  } = ctx;

  const facilities = resolveSecuredFacilities(pnl, assumptions);
  const salesPct = salesRow.salesPct;
  const constructionPct = constructionPctAtMonth(month, programme, ctx.costSchedule);

  const depositGross = salesRow.revenueDelta * assumptions.depositRate;
  const usableFromDeposit = depositGross * assumptions.depositUsablePct;
  const restrictedFromDeposit = depositGross - usableFromDeposit;

  let deliveryBuyerCash = 0;
  let restrictedRelease = 0;
  if (month === programme.deliveryMonth) {
    const totalDeposits = pnl.salesRevenue * assumptions.depositRate;
    const alreadyUsableDeposits = totalDeposits * assumptions.depositUsablePct;
    const remainingSaleCash = Math.max(0, pnl.salesRevenue - totalDeposits);
    deliveryBuyerCash = remainingSaleCash + (totalDeposits - alreadyUsableDeposits);
    restrictedRelease = state.restrictedBuyerCash;
  }

  const usableBuyerCash = usableFromDeposit + deliveryBuyerCash;
  const restrictedBuyerCashDelta = month === programme.deliveryMonth
    ? -restrictedRelease
    : restrictedFromDeposit;

  const landCostNow =
    (costs.detail.landPrice || 0) + (costs.detail.acquisitionCosts || 0);
  const constructionCostNow =
    (costs.detail.constructionCost || 0) + (costs.detail.constructionReserve || 0);

  const equityInjected = pnl.sponsorEquity + state.cumulativeSpvContributions;

  const sharedCap = facilities.sharedBankFacility;
  const landCap = facilities.landSecured;
  const constructionCap = facilities.constructionSecured;
  const landDrawnToDate = state.landFacilityDrawn || 0;
  const sharedDrawnToDate = state.sharedFacilityDrawn || 0;
  const securedConstructionDrawnToDate = state.securedConstructionFacilityDrawn || 0;
  const projectedConstructionDrawnToDate = state.projectedConstructionFacilityDrawn || 0;

  const unmet = [];
  let bankDraw = 0;
  let landFacilityDraw = 0;
  let constructionFacilityDraw = 0;
  let sharedFacilityDraw = 0;
  let securedConstructionFacilityDraw = 0;
  let projectedConstructionFacilityDraw = 0;

  // Land facility: own conditions at acquisition — not permit + construction presales.
  if (landCostNow > 1e-9) {
    const sharedHeadroom = Math.max(0, sharedCap - sharedDrawnToDate);
    const dedicatedLandHeadroom = Math.max(0, landCap - landDrawnToDate);
    const landHeadroom = sharedHeadroom + dedicatedLandHeadroom;
    const check = evaluateLandBankConditions({
      month,
      programme,
      facilitySecured: landHeadroom,
      eligibleCostsIncurred: landCostNow + state.cumulativeLandCosts,
      drawnToDate: 0,
      requestedDraw: landCostNow,
      landRequiresRentalSelfSufficiency: assumptions.landRequiresRentalSelfSufficiency,
      rentalSelfSufficient: assumptions.rentalSelfSufficient,
    });
    if (check.met) {
      const draw = Math.min(landCostNow, landHeadroom);
      bankDraw += draw;
      landFacilityDraw += draw;
      const fromShared = Math.min(draw, sharedHeadroom);
      sharedFacilityDraw += fromShared;
    } else {
      for (const u of check.unmet) unmet.push(`Land facility: ${u}`);
    }
  }

  // Construction credit: permit + presale threshold + equity.
  const constructionFundingNeed = Math.max(
    0,
    Math.min(
      constructionCostNow,
      assumptions.minCashBuffer + costs.total
        - (n(ctx.availableSpvCash) + bankDraw + usableBuyerCash),
    ),
  );
  if (constructionFundingNeed > 1e-9) {
    const sharedHeadroom = Math.max(
      0,
      sharedCap - sharedDrawnToDate - sharedFacilityDraw,
    );
    const securedHeadroom = Math.max(
      0,
      constructionCap - securedConstructionDrawnToDate,
    );
    const projectedHeadroom = Math.max(
      0,
      Math.max(0, n(plannedConstructionFacility)) - projectedConstructionDrawnToDate,
    );
    const constructionHeadroom = sharedHeadroom + securedHeadroom + projectedHeadroom;
    const check = evaluateConstructionBankConditions({
      month,
      programme,
      salesPct,
      threshold: assumptions.bankActivationThreshold,
      sponsorEquityInjected: equityInjected,
      requiredSponsorEquity: pnl.sponsorEquity,
      facilitySecured: constructionHeadroom,
      eligibleCostsIncurred: constructionCostNow + state.cumulativeConstructionCosts,
      drawnToDate: 0,
      requestedDraw: constructionFundingNeed,
    });
    if (check.met) {
      const draw = Math.min(constructionFundingNeed, constructionHeadroom);
      bankDraw += draw;
      constructionFacilityDraw += draw;
      const fromShared = Math.min(draw, sharedHeadroom);
      const afterShared = draw - fromShared;
      const fromSecured = Math.min(afterShared, securedHeadroom);
      const fromProjected = Math.max(0, afterShared - fromSecured);
      sharedFacilityDraw += fromShared;
      securedConstructionFacilityDraw += fromSecured;
      projectedConstructionFacilityDraw += fromProjected;
    } else {
      for (const u of check.unmet) unmet.push(`Construction facility: ${u}`);
    }
  }

  let partnerDraw = 0;
  const partnerHeadroom = Math.max(0, facilities.privateSecured - state.partnerDrawn);
  const cashBeforePartner =
    n(ctx.availableSpvCash) + bankDraw + usableBuyerCash - costs.total;
  const uncovered = Math.max(0, assumptions.minCashBuffer - cashBeforePartner);
  if (partnerHeadroom > 0 && uncovered > 0) {
    partnerDraw = Math.min(partnerHeadroom, uncovered);
  }

  return {
    bankDraw,
    partnerDraw,
    landFacilityDraw,
    constructionFacilityDraw,
    sharedFacilityDraw,
    securedConstructionFacilityDraw,
    projectedConstructionFacilityDraw,
    usableBuyerCash,
    restrictedBuyerCashDelta,
    buyerDirectConstruction: 0,
    buyerDirectLandSettlement: 0,
    repayAtDelivery: month === programme.deliveryMonth,
    salesPct,
    constructionPct,
    unmetBankConditions: [...new Set(unmet)],
    facilities,
    landCostNow,
    constructionCostNow,
  };
}

/**
 * Independent survival calc: construction facility required vs committed in P&L.
 */
export function computeVatConstructionFacilityRequired({
  pnl,
  programme,
  assumptions,
  costSchedule,
  salesSchedule,
}) {
  const facilities = resolveSecuredFacilities(pnl, assumptions);
  let cash = pnl.sponsorEquity + Math.max(0, n(pnl.feesFunding ?? pnl.upfrontFees));
  let peakShortfall = 0;
  let runningCredit = 0;
  let mustBeAvailableMonth = null;
  let landFacilityDrawn = 0;
  let nonBankableSponsorRequired = 0;

  const landFacility = facilities.sharedBankFacility + facilities.landSecured;
  let remainingFeasibilityCapital = Math.max(0, n(pnl.feasibilityCapital ?? pnl.feasibilityCosts));

  for (let month = 1; month <= programme.totalMonths; month += 1) {
    const salesRow = salesSchedule.months[month - 1];
    const build =
      (costSchedule.byCategory.constructionCost?.[month - 1] || 0) +
      (costSchedule.byCategory.constructionReserve?.[month - 1] || 0);
    const land =
      (costSchedule.byCategory.landPrice?.[month - 1] || 0) +
      (costSchedule.byCategory.acquisitionCosts?.[month - 1] || 0);
    const other = (costSchedule.totalByMonth[month - 1] || 0) - build - land;

    // Financing and feasibility are genuine costs; match staged capital when due.
    // Fees funding already sits in opening cash — never invent a Fees expense.
    const financingDue = costSchedule.byCategory.financingCosts?.[month - 1] || 0;
    const feasDue = costSchedule.byCategory.feasibilityCosts?.[month - 1] || 0;
    const feasibilityIn = Math.min(feasDue, remainingFeasibilityCapital);
    remainingFeasibilityCapital = Math.max(0, remainingFeasibilityCapital - feasibilityIn);
    const capitalIn = financingDue + feasibilityIn;

    const depositGross = salesRow.revenueDelta * assumptions.depositRate;
    let buyerIn = depositGross * assumptions.depositUsablePct;
    if (month === programme.deliveryMonth) {
      const totalDeposits = pnl.salesRevenue * assumptions.depositRate;
      buyerIn += Math.max(0, pnl.salesRevenue - totalDeposits)
        + totalDeposits * (1 - assumptions.depositUsablePct);
    }

    const constructionActivated =
      month >= programme.permitMonth &&
      salesRow.salesPct + 1e-9 >= assumptions.bankActivationThreshold;

    // Land facility draws at acquisition under land conditions (not permit/presales).
    const landReady =
      month >= programme.acquisitionMonth &&
      (!assumptions.landRequiresRentalSelfSufficiency || assumptions.rentalSelfSufficient);

    let landBank = 0;
    if (landReady && land > 0 && landFacility > 0) {
      landBank = Math.min(land, Math.max(0, landFacility - landFacilityDrawn));
      landFacilityDrawn += landBank;
    }

    // Staged capital + buyer + land bank − P&L cash costs only.
    cash += capitalIn + buyerIn + landBank - land - other - build;

    if (cash < assumptions.minCashBuffer - 1e-9) {
      let need = assumptions.minCashBuffer - cash;

      // A construction facility can only fund eligible construction after its
      // bank conditions are met. Preparation/authorization and blocked months
      // remain sponsor cash exposure; do not disguise them as bank debt.
      if (constructionActivated && build > 1e-9) {
        const bankEligibleNeed = Math.min(need, build);
        runningCredit += bankEligibleNeed;
        cash += bankEligibleNeed;
        need -= bankEligibleNeed;
        peakShortfall = Math.max(peakShortfall, runningCredit);
        if (bankEligibleNeed > 0 && mustBeAvailableMonth == null) {
          mustBeAvailableMonth = month;
        }
      }

      if (need > 1e-9) {
        nonBankableSponsorRequired += need;
        cash += need;
      }
    }
  }

  const unusedSharedCapacity = Math.max(
    0,
    facilities.sharedBankFacility - landFacilityDrawn,
  );
  const securedEffective = unusedSharedCapacity + facilities.constructionSecured;

  const alreadySecuredLandFacility = Math.max(0, landFacilityDrawn);

  return {
    constructionFacilityRequired: peakShortfall,
    constructionFacilitySecured: securedEffective,
    remainingBankFundingGap: Math.max(0, peakShortfall - securedEffective),
    facilityMustBeAvailableMonth: mustBeAvailableMonth || programme.constructionStart,
    alreadySecuredLandFacility,
    nonBankableSponsorRequired,
  };
}
